import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';
import { useBrandingStore } from '@/stores/brandingStore';

const SR = (v) => `SR ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PCT = (v) => `${(v || 0).toFixed(1)}%`;

function companyName() {
  try { return useBrandingStore.getState().branding?.app_name || ''; } catch { return ''; }
}

// Shared clean header: company · title · period/branch line · generated time. Used by every PDF export.
function addHeader(doc, title, subtitle) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(title, 10, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(subtitle || '', 10, 17);
  doc.setFont('helvetica', 'bold');
  doc.text(companyName(), w - 10, 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(`Generated ${new Date().toLocaleString()}`, w - 10, 17, { align: 'right' });
  doc.setTextColor(40);
}

function addFooter(doc) {
  const pages = doc.internal.getNumberOfPages();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(220);
    doc.line(10, h - 10, w - 10, h - 10);
    doc.setFontSize(6.5);
    doc.setTextColor(150);
    doc.setFont('helvetica', 'normal');
    doc.text('Powered by SmartoERP', 10, h - 6);
    doc.text(`Page ${i} of ${pages}`, w - 10, h - 6, { align: 'right' });
  }
}

// Statement table: [label, amount] rows with section/total/sub styles; numbers right-aligned.
function statementTable(doc, startY, title, rows, opts = {}) {
  autoTable(doc, {
    startY,
    head: [[title, opts.amountHead || 'Amount (SR)']],
    body: rows.map((r) => [String(r[0]).replace(/[\u2212\u2014]/g, '-'), r[1] == null || r[1] === '' ? '' : (typeof r[1] === 'string' ? r[1] : SR(r[1]))]),
    margin: opts.margin || { left: 12, right: 12 },
    showHead: 'everyPage',
    styles: { fontSize: opts.fontSize || 8.5, overflow: 'linebreak', cellPadding: { top: 1.2, bottom: 1.2, left: 2.5, right: 2.5 }, lineColor: [235, 235, 235], lineWidth: 0.1 },
    headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold', fontSize: opts.fontSize || 8.5 },
    columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: opts.amountWidth || 42, halign: 'right' } },
    rowPageBreak: 'avoid',
    didParseCell: (h) => {
      const r = rows[h.row.index];
      if (h.section !== 'body' || !r) return;
      if (r[2] === 'total') { h.cell.styles.fontStyle = 'bold'; h.cell.styles.fillColor = [248, 250, 252]; h.cell.styles.fontSize = (opts.fontSize || 8.5) + 0.5; }
      else if (r[2] === 'section') { h.cell.styles.fontStyle = 'bold'; }
      else if (r[2] === 'sub') { h.cell.styles.textColor = [110, 110, 110]; h.cell.styles.fontSize = (opts.fontSize || 8.5) - 1; }
      else if (r[2] === 'warn') { h.cell.styles.textColor = [180, 83, 9]; h.cell.styles.fontSize = (opts.fontSize || 8.5) - 0.5; }
    },
  });
  return doc.lastAutoTable.finalY + 4;
}

// Only move to a new page when not even a heading + 3 rows fit (avoids large blank areas).
function ensureSpace(doc, y, need = 22) {
  if (y > doc.internal.pageSize.getHeight() - need) { doc.addPage(); return 14; }
  return y;
}

// Two statement tables side by side (half page each); returns the lower end.
function sideBySide(doc, y, left, right) {
  const w = doc.internal.pageSize.getWidth();
  const half = { amountWidth: 30, fontSize: 7.8 };
  const p = doc.internal.getCurrentPageInfo().pageNumber;
  const y1 = statementTable(doc, y, left[0], left[1], { ...half, margin: { left: 12, right: w / 2 + 2 } });
  const p1 = doc.internal.getCurrentPageInfo().pageNumber;
  doc.setPage(p);
  const y2 = statementTable(doc, y, right[0], right[1], { ...half, margin: { left: w / 2 + 2, right: 12 } });
  const p2 = doc.internal.getCurrentPageInfo().pageNumber;
  const last = Math.max(p1, p2);
  doc.setPage(last);
  return last === p1 && last === p2 ? Math.max(y1, y2) : last === p1 ? y1 : y2;
}

const VSTATUS = { STATEMENT_NOT_RECONCILED: 'UNVERIFIED - statement not reconciled', STALE: 'UNVERIFIED - statement out of date', NO_STATEMENT: 'UNVERIFIED - no statement' };

// Verified money (reconciled statement / cashier count) vs calculated GL balances — never mixed in one total.
function cashBankRows(cb) {
  const v = cb?.verified || {};
  const any = v.bank_total_verified != null || v.cash_total_verified != null;
  return [
    ['VERIFIED cash + bank', any ? (v.bank_total_verified || 0) + (v.cash_total_verified || 0) : 'None verified', 'total'],
    ...(v.bank_accounts || []).map((a) => [`   ${a.name}`, a.verified ? a.balance : `${VSTATUS[a.status] || 'UNVERIFIED'}${a.statement_balance != null ? ` (stmt ${SR(a.statement_balance)})` : ''}`, a.verified ? '' : 'warn']),
    ...(v.cash_counts || []).filter((c) => c.verified).map((c) => [`   Cash count ${c.as_of}`, c.balance]),
    ['Calculated (GL) - UNVERIFIED, not available funds', '', 'section'],
    ...['cash', 'bank'].flatMap((k) => cb?.[k] ? [[`   ${k === 'cash' ? 'Cash' : 'Bank'}: opening ${SR(cb[k].opening)} + in ${SR(cb[k].received)} - out ${SR(cb[k].paid)}`, cb[k].closing, 'sub']] : []),
  ];
}

function supplierRows(sc) {
  return [
    ['Purchases paid immediately', sc.paid_immediately], ['New credit purchases', sc.new_credit],
    ['Paid against this period\'s credit', sc.paid_current_credit], ['Remaining credit from this period', sc.remaining_current_credit, 'total'],
    ['Credit at period start', sc.opening_outstanding, 'sub'], ['Paid against older credit (not an expense)', sc.paid_previous_credit],
    ...(Math.abs(sc.advance_or_unmatched || 0) >= 0.01 ? [['Advances / payments without a bill', sc.advance_or_unmatched, 'sub']] : []),
    ['Supplier outstanding at period end', sc.closing_outstanding, 'total'],
  ];
}

/**
 * Export P&L — same response as the screen (posted GL headline, VAT lines, reconciliation, cash/bank/supplier credit).
 */
export function exportPnLToPDF(data, title, period, opts = {}) {
  if (!data) { toast.error('No data to export'); return; }
  toast.info('Generating PDF...');
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const incl = !!opts.inclVat;
    addHeader(doc, title || 'Profit & Loss Statement', `${period || ''}${opts.branchLabel ? ` · ${opts.branchLabel}` : ''} · ${incl ? 'Incl. VAT' : 'Excl. VAT'} · basis: posted General Ledger`);
    const rv = data.revenue || {};
    const ib = data.inter_branch_margin;
    const comp = data.completeness || {};
    const rows = [
      [incl ? 'REVENUE (incl. VAT)' : 'REVENUE (excl. VAT)', incl ? rv.gross_incl_vat : rv.total, 'section'],
      ['Net sales (excl. VAT)', rv.total],
      ['VAT collected (liability, not income)', rv.output_vat_gl, 'sub'],
      ['COST OF SALES', data.cost_of_sales?.total, 'section'],
      ...(data.cost_of_sales?.items || []).map((i) => [`   ${i.name}`, i.total, 'sub']),
      ['GROSS PROFIT', data.gross_profit, 'total'],
      ['OPERATING EXPENSES (excl. VAT)', data.operating_expenses?.total, 'section'],
      ...Object.entries(data.operating_expenses?.by_group || {}).flatMap(([g, info]) => [[`   ${g}`, info.total], ...(info.items || []).map((i) => [`      ${i.name}`, i.total, 'sub'])]),
      ['OPERATING PROFIT', data.operating_profit ?? data.net_profit, 'total'],
      ...(ib && Math.abs(ib.amount) >= 0.01 ? [[`Inter-branch invoice margin (${ib.included_in_profit ? 'included' : 'eliminated - internal, not added'})`, ib.amount, ib.included_in_profit ? '' : 'sub']] : []),
      [data.net_profit >= 0 ? 'NET PROFIT (accounting, excl. VAT)' : 'NET LOSS (accounting, excl. VAT)', data.net_profit, 'total'],
      ['Net margin', data.net_margin != null ? `${data.net_margin.toFixed(1)}%` : 'n/a', 'sub'],
      ...(data.margin_note ? [[data.margin_note, '', 'warn']] : []),
      ...(incl ? [['Input VAT on purchases & expenses (reclaimable, not in costs)', data.input_vat_gl, 'sub']] : []),
      ...(data.negative_accounts || []).map((a) => [`Review: ${a.code} ${a.name} has a credit balance`, a.amount, 'warn']),
    ];
    let y = statementTable(doc, 28, 'Profit & Loss', rows);
    if (comp && comp.gl_complete === false) {
      y = ensureSpace(doc, y);
      y = statementTable(doc, y, 'General Ledger completeness', [
        ...(comp.unposted_sales_count ? [[`Sales not posted to the GL (${comp.unposted_sales_count})`, comp.unposted_sales_net]] : []),
        ...(comp.unposted_costs_count ? [[`Expenses / salaries not posted to the GL (${comp.unposted_costs_count})`, comp.unposted_costs]] : []),
        ['ESTIMATE (not the accounting result): net profit if missing records were posted', comp.net_including_unposted, 'warn'],
        ['ESTIMATE: margin if missing records were posted', comp.margin_including_unposted != null ? `${comp.margin_including_unposted}%` : 'n/a', 'sub'],
        ['Official result = posted GL net profit above. Nothing is posted automatically.', '', 'sub'],
      ]);
    }
    const rec = data.reconciliation;
    if (rec) {
      y = ensureSpace(doc, y);
      y = statementTable(doc, y, 'Reconciliation: operational records to posted GL', [
        ['Operational net profit (daily records, excl. VAT)', rec.operational_net],
        ...rec.items.filter((i) => Math.abs(i.effect_on_profit) >= 0.01).map((i) => [`${i.effect_on_profit >= 0 ? '+' : '-'} ${i.label}${i.count ? ` (${i.count})` : ''}`, Math.abs(i.effect_on_profit), 'sub']),
        ['Accounting net profit (posted GL)', rec.gl_net, 'total'],
      ]);
    }
    const sup = data.supplementary;
    if (sup) {
      y = ensureSpace(doc, y, 35);
      y = statementTable(doc, y, 'Cash and bank (does not change profit)', cashBankRows(sup.cash_bank));
      y = ensureSpace(doc, y, 35);
      statementTable(doc, y, 'Supplier purchases and credit', supplierRows(sup.supplier_credit));
    }
    addFooter(doc);
    doc.save(`${(title || 'pnl').replace(/\s+/g, '_').toLowerCase()}_${localToday()}.pdf`);
    toast.success('PDF exported');
  } catch (err) {
    console.error(err);
    toast.error('Failed to generate PDF');
  }
}

/**
 * Export Branch P&L comparison as PDF
 */
export function exportBranchPnLToPDF(data, title, period) {
  if (!data?.branches?.length) { toast.error('No data'); return; }
  toast.info('Generating PDF...');

  try {
    const doc = new jsPDF('l', 'mm', 'a4');
    addHeader(doc, title || 'Profit & Loss by Branch', period || '');

    const g = data.grand_total || {};
    const branches = data.branches;

    // Comparison table
    autoTable(doc, {
      startY: 30,
      head: [['Branch', 'Sales', 'Revenue', 'COGS', 'Gross Profit', 'OpEx', 'Net Profit', 'Margin']],
      body: [
        ...branches.map(b => [
          b.branch_name,
          String(b.sales_count),
          SR(b.revenue),
          SR(b.cost_of_sales),
          SR(b.gross_profit),
          SR(b.operating_expenses),
          SR(b.net_profit),
          PCT(b.net_margin),
        ]),
        [
          { content: 'TOTAL', styles: { fontStyle: 'bold' } },
          { content: String(branches.reduce((s, x) => s + x.sales_count, 0)), styles: { fontStyle: 'bold' } },
          { content: SR(g.revenue), styles: { fontStyle: 'bold' } },
          { content: SR(g.cost_of_sales), styles: { fontStyle: 'bold' } },
          { content: SR(g.revenue - (g.cost_of_sales || 0)), styles: { fontStyle: 'bold' } },
          { content: SR(g.operating_expenses), styles: { fontStyle: 'bold' } },
          { content: SR(g.net_profit), styles: { fontStyle: 'bold', textColor: g.net_profit >= 0 ? [21, 128, 61] : [185, 28, 28] } },
          { content: g.revenue ? PCT(g.net_profit / g.revenue * 100) : '0%', styles: { fontStyle: 'bold' } },
        ],
      ],
      margin: { left: 10, right: 10 },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'center', cellWidth: 18 },
        2: { halign: 'right', cellWidth: 32 },
        3: { halign: 'right', cellWidth: 32 },
        4: { halign: 'right', cellWidth: 32 },
        5: { halign: 'right', cellWidth: 32 },
        6: { halign: 'right', cellWidth: 32 },
        7: { halign: 'center', cellWidth: 20 },
      },
      didParseCell: (hookData) => {
        if (hookData.row.index === branches.length) {
          hookData.cell.styles.fillColor = [241, 245, 249];
        }
      },
    });

    // Individual branch details
    let y = doc.lastAutoTable.finalY + 10;
    branches.forEach(br => {
      if (y > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 15; }

      const rows = [
        ['Revenue', '', SR(br.revenue)],
        ['Cost of Sales', PCT(br.revenue ? br.cost_of_sales / br.revenue * 100 : 0), `(${SR(br.cost_of_sales)})`],
        ['  Supplier Purchases', '', SR(br.supplier_purchases || 0)],
        ['  Supplier Expenses', '', SR(br.supplier_expenses || 0)],
        ['Gross Profit', PCT(br.gross_margin), SR(br.gross_profit)],
        ['Operating Expenses', PCT(br.revenue ? br.operating_expenses / br.revenue * 100 : 0), `(${SR(br.operating_expenses)})`],
        ...Object.entries(br.expense_categories || {}).filter(([k]) => !['Supplier', 'Supplier Purchase', 'supplier'].includes(k)).map(([k, v]) => [`  ${k}`, '', SR(v)]),
        ['Net Profit / (Loss)', PCT(br.net_margin), SR(br.net_profit)],
      ];

      autoTable(doc, {
        startY: y,
        head: [[{ content: `${br.branch_name} — Detail`, colSpan: 3, styles: { fillColor: [59, 130, 246] } }]],
        body: rows,
        margin: { left: 10, right: 10 },
        styles: { fontSize: 7, cellPadding: 1.8 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 35, halign: 'right', fontStyle: 'bold' } },
        didParseCell: (hookData) => {
          const label = rows[hookData.row.index]?.[0] || '';
          if (label === 'Gross Profit') { hookData.cell.styles.fillColor = [240, 249, 255]; hookData.cell.styles.fontStyle = 'bold'; }
          if (label.includes('Net Profit')) { hookData.cell.styles.fillColor = br.net_profit >= 0 ? [240, 253, 244] : [254, 242, 242]; hookData.cell.styles.fontStyle = 'bold'; }
        },
      });
      y = doc.lastAutoTable.finalY + 6;
    });

    addFooter(doc);
    doc.save(`branch_pnl_${localToday()}.pdf`);
    toast.success('PDF exported');
  } catch (err) { console.error(err); toast.error('Failed'); }
}

/**
 * Generic table-based PDF export (for any report)
 */
export function exportTableToPDF(title, subtitle, headers, rows, options = {}) {
  toast.info('Generating PDF...');
  try {
    const doc = new jsPDF(options.landscape ? 'l' : 'p', 'mm', 'a4');
    addHeader(doc, title, subtitle);

    autoTable(doc, {
      startY: 30,
      head: [headers],
      body: rows,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      ...(options.columnStyles || {}),
    });

    addFooter(doc);
    doc.save(options.filename ? `${options.filename}.pdf` : `${title.replace(/\s+/g, '_').toLowerCase()}_${localToday()}.pdf`);
    toast.success('PDF exported');
  } catch (err) { console.error(err); toast.error('Failed'); }
}

/**
 * Export Balance Sheet as tabular PDF
 */
export function exportBalanceSheetToPDF(data, title, asOf) {
  if (!data) { toast.error('No data to export'); return; }
  toast.info('Generating PDF...');
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    addHeader(doc, title || 'Balance Sheet', asOf ? `As of ${asOf}` : '');

    const rows = [];
    const pushSection = (label, amount) => rows.push({ label, amount: SR(amount), style: 'section' });
    const pushItem = (label, amount) => rows.push({ label: `  ${label}`, amount: SR(amount), style: 'item' });
    const pushSub = (label, amount) => rows.push({ label: `    ${label}`, amount: SR(amount), style: 'sub' });
    const pushTotal = (label, amount) => rows.push({ label, amount: SR(amount), style: 'total' });

    const walkSection = (title2, group, total) => {
      pushSection(title2, total);
      Object.entries(group || {}).forEach(([section, entries]) => {
        if (!entries || !Object.keys(entries).length) return;
        pushItem(section.replace(/_/g, ' '), Object.values(entries).reduce((s, v) => s + (v || 0), 0));
        Object.entries(entries).forEach(([n, v]) => pushSub(n, v));
      });
    };

    // F1 — read the API's real fields (was total_assets/total_equity/balanced → always 0.00).
    const tA = data.assets?.total ?? data.total_assets ?? 0;
    const tL = data.liabilities?.total ?? data.total_liabilities ?? 0;
    const tE = data.equity?.total ?? data.total_equity ?? 0;
    const tLE = data.total_liabilities_equity ?? (tL + tE);
    walkSection('ASSETS', data.assets, tA);
    walkSection('LIABILITIES', data.liabilities, tL);
    walkSection('EQUITY', data.equity, tE);
    pushTotal('TOTAL LIABILITIES + EQUITY', tLE);
    const balanced = data.is_balanced ?? data.balanced;
    pushTotal(balanced ? 'BALANCED ✓' : 'NOT BALANCED — DIFFERENCE', balanced ? tA : tA - tLE);
    if (Math.abs(data.unposted_difference || 0) >= 1) pushTotal('UNPOSTED LEDGER DIFFERENCE', data.unposted_difference);

    const body = rows.map(r => [r.label, r.amount]);
    autoTable(doc, {
      startY: 30,
      head: [['Description', 'Amount (SR)']],
      body,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 45, halign: 'right', fontStyle: 'bold' },
      },
      didParseCell: (hookData) => {
        const r = rows[hookData.row.index];
        if (!r) return;
        if (r.style === 'section') {
          hookData.cell.styles.fillColor = [241, 245, 249];
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.textColor = [15, 23, 42];
        } else if (r.style === 'total') {
          hookData.cell.styles.fillColor = [240, 253, 244];
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.fontSize = 9;
          hookData.cell.styles.textColor = [21, 128, 61];
        } else if (r.style === 'sub') {
          hookData.cell.styles.textColor = [140, 140, 140];
          hookData.cell.styles.fontSize = 7;
        }
      },
    });
    addFooter(doc);
    doc.save(`balance_sheet_${asOf || localToday()}.pdf`);
    toast.success('PDF exported');
  } catch (err) { console.error(err); toast.error('Failed'); }
}

/**
 * iter951 — Accountant's Tie-Out Pack: ONE PDF bundling the Balance Sheet,
 * Trial Balance and Account Summary for a period, plus a reconciliation
 * summary page. Reuses addHeader/addFooter/autoTable/SR (same engine as the
 * invoice + statement PDFs). Caller passes the three already-fetched datasets.
 */
export function exportTieOutPackToPDF({ balanceSheet, trialBalance, accountSummary, periodLabel }) {
  toast.info('Generating Tie-Out Pack...');
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const period = periodLabel || 'All time';

    // ── Page 1: Reconciliation summary ──────────────────────────────
    addHeader(doc, 'Accounting Tie-Out Pack', period);
    const tbBal = Math.abs((trialBalance?.total_debits || 0) - (trialBalance?.total_credits || 0)) < 0.01;
    const asBal = Math.abs((accountSummary?.total_debit || 0) - (accountSummary?.total_credit || 0)) < 0.01;
    const bsBal = Math.abs((balanceSheet?.assets?.total || 0) - (balanceSheet?.total_liabilities_equity || 0)) < 0.01;
    autoTable(doc, {
      startY: 30,
      head: [['Statement', 'Debits / Assets', 'Credits / L+E', 'Status']],
      body: [
        ['Trial Balance', SR(trialBalance?.total_debits), SR(trialBalance?.total_credits), tbBal ? 'Reconciled' : 'Out of Balance'],
        ['Account Summary', SR(accountSummary?.total_debit), SR(accountSummary?.total_credit), asBal ? 'Reconciled' : 'Out of Balance'],
        ['Balance Sheet', SR(balanceSheet?.assets?.total), SR(balanceSheet?.total_liabilities_equity), bsBal ? 'Reconciled' : 'Out of Balance'],
      ],
      margin: { left: 10, right: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'center', fontStyle: 'bold' } },
      didParseCell: (h) => {
        if (h.section === 'body' && h.column.index === 3) {
          const ok = h.cell.raw === 'Reconciled';
          h.cell.styles.textColor = ok ? [21, 128, 61] : [185, 28, 28];
        }
      },
    });

    // ── Page 2: Trial Balance ───────────────────────────────────────
    doc.addPage(); addHeader(doc, 'Trial Balance', period);
    const tbRows = (trialBalance?.accounts || []).map(a => [a.code, a.name, a.debit > 0 ? SR(a.debit) : '-', a.credit > 0 ? SR(a.credit) : '-']);
    tbRows.push(['', 'TOTAL', SR(trialBalance?.total_debits), SR(trialBalance?.total_credits)]);
    autoTable(doc, {
      startY: 30, head: [['Code', 'Account', 'Debit', 'Credit']], body: tbRows,
      margin: { left: 10, right: 10 }, styles: { fontSize: 7.5, cellPadding: 1.8 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 7 },
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' } },
      didParseCell: (h) => { if (h.section === 'body' && h.row.index === tbRows.length - 1) { h.cell.styles.fontStyle = 'bold'; h.cell.styles.fillColor = [241, 245, 249]; } },
    });

    // ── Page 3: Account Summary ─────────────────────────────────────
    doc.addPage(); addHeader(doc, 'Account Summary', period);
    const asRows = (accountSummary?.accounts || []).map(a => [a.code, a.name, SR(a.gross_debit), SR(a.gross_credit), SR(a.balance)]);
    asRows.push(['', 'GRAND TOTAL (Net)', SR(accountSummary?.total_debit), SR(accountSummary?.total_credit), '']);
    autoTable(doc, {
      startY: 30, head: [['Code', 'Account', 'Total Debits', 'Total Credits', 'Net Balance']], body: asRows,
      margin: { left: 10, right: 10 }, styles: { fontSize: 7.5, cellPadding: 1.8 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 7 },
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' } },
      didParseCell: (h) => { if (h.section === 'body' && h.row.index === asRows.length - 1) { h.cell.styles.fontStyle = 'bold'; h.cell.styles.fillColor = [241, 245, 249]; } },
    });

    // ── Page 4: Balance Sheet ───────────────────────────────────────
    doc.addPage(); addHeader(doc, 'Balance Sheet', balanceSheet?.as_of_date ? `As of ${balanceSheet.as_of_date}` : period);
    const bsRows = [];
    const addGroup = (label, total, dict) => {
      bsRows.push({ label, amount: SR(total), style: 'section' });
      Object.entries(dict || {}).forEach(([n, v]) => bsRows.push({ label: '  ' + n, amount: SR(v), style: 'item' }));
    };
    addGroup('ASSETS', balanceSheet?.assets?.total, { ...(balanceSheet?.assets?.current_assets || {}), ...(balanceSheet?.assets?.fixed_assets || {}) });
    addGroup('LIABILITIES', balanceSheet?.liabilities?.total, { ...(balanceSheet?.liabilities?.current_liabilities || {}), ...(balanceSheet?.liabilities?.long_term_liabilities || {}) });
    addGroup('EQUITY', balanceSheet?.equity?.total, balanceSheet?.equity?.items || {});
    bsRows.push({ label: 'TOTAL LIABILITIES + EQUITY', amount: SR(balanceSheet?.total_liabilities_equity), style: 'total' });
    autoTable(doc, {
      startY: 30, head: [['Description', 'Amount (SR)']], body: bsRows.map(r => [r.label, r.amount]),
      margin: { left: 10, right: 10 }, styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 7 },
      columnStyles: { 1: { cellWidth: 45, halign: 'right', fontStyle: 'bold' } },
      didParseCell: (h) => {
        const r = bsRows[h.row.index]; if (!r) return;
        if (r.style === 'section') { h.cell.styles.fillColor = [241, 245, 249]; h.cell.styles.fontStyle = 'bold'; }
        else if (r.style === 'total') { h.cell.styles.fillColor = [240, 253, 244]; h.cell.styles.fontStyle = 'bold'; h.cell.styles.textColor = [21, 128, 61]; }
      },
    });

    addFooter(doc);
    doc.save(`tie_out_pack_${String(periodLabel || localToday()).replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`);
    toast.success('Tie-Out Pack exported');
  } catch (err) { console.error(err); toast.error('Failed to generate Tie-Out Pack'); }
}


/**
 * Export Financial Ratios / Dashboard-style metrics as PDF
 */
export function exportMetricsToPDF(title, subtitle, metrics) {
  toast.info('Generating PDF...');
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    addHeader(doc, title, subtitle || '');
    const body = (metrics || []).map(m => [m.label, m.value, m.benchmark || '-', m.status || '-']);
    autoTable(doc, {
      startY: 30,
      head: [['Metric', 'Value', 'Benchmark', 'Status']],
      body,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        1: { halign: 'right', fontStyle: 'bold' },
        2: { halign: 'center' },
        3: { halign: 'center' },
      },
    });
    addFooter(doc);
    doc.save(`${title.replace(/\s+/g, '_').toLowerCase()}_${localToday()}.pdf`);
    toast.success('PDF exported');
  } catch (err) { console.error(err); toast.error('Failed'); }
}

/**
 * Export Comprehensive Branch Summary Report
 * Sales (Cash/Bank/Online/Customer/Total) → COGS → Balance → Expenses → Net
 */
export function exportBranchSummaryToPDF(data, periodLabel, opts = {}) {
  if (!data?.branches?.length) { toast.error('No data to export'); return; }
  toast.info('Generating PDF...');
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const g = data.grand_total || {};
    const s = g.sales || {};
    const ov = data.overview || {};
    const sc = ov.supplier_credit || {};
    const p = g.profit || {};
    const per = data.period ? `${data.period.start} to ${data.period.end}` : (periodLabel || '');
    addHeader(doc, 'Branch Summary', `${per} · ${opts.branchNames || 'All branches'} · excl. VAT · costs in the branch that benefits`);
    const c = g.collections || {};
    let y = statementTable(doc, 28, 'Business performance (excl. VAT)', [
      ['Net sales', p.net_sales ?? s.net, 'section'],
      ['Gross sales (incl. VAT)', s.gross, 'sub'], ['Output VAT', s.vat, 'sub'],
      ['− Cost of goods (purchase-based)', p.cogs ?? g.cogs], ['= Gross profit', p.gross_profit, 'section'],
      ['− Operating expenses', p.operating_expenses ?? g.expenses_total],
      ...((p.paid_by_other_branches || 0) >= 0.01 ? [['   of which paid by other branches', p.paid_by_other_branches, 'sub']] : []),
      ['= Net profit', g.net, 'total'], ['Margin (net profit / net sales)', `${g.net_margin ?? 0}%`, 'sub'],
      ...(ov.gl ? [['Accounting net profit (posted GL)', ov.gl.net_profit, 'sub']] : []),
    ]);
    y = ensureSpace(doc, y, 40);
    y = sideBySide(doc, y, ['Sales value vs money (incl. VAT)', [
      ['Cash received', c.cash], ['Bank received', c.bank], ['Card (awaiting settlement)', c.card_awaiting_settlement], ['Delivery platforms (receivable)', c.platform_sales],
      ['Customer credit (unpaid balance)', c.customer_credit],
      ['Unresolved difference', c.not_matched_to_payment, Math.abs(c.not_matched_to_payment || 0) >= 0.01 ? 'warn' : ''],
      ...(c.unmatched_count ? [[`   short-paid ${SR(c.short_paid)} (${c.short_paid_count}) / paid above total ${SR(c.paid_above_total)} (${c.paid_above_total_count})`, '', 'sub']] : []),
      ['Sales value (incl. VAT)', c.total_sales_incl_vat, 'total'],
      ['Platform receivable pending', c.platform_pending, 'sub'],
      ...((c.platform_settled_above_recorded_sales || 0) >= 0.01 ? [['Platform settled above recorded sales - check', c.platform_settled_above_recorded_sales, 'warn']] : []),
    ]], ['Supplier purchases and credit', supplierRows(sc)]);
    y = ensureSpace(doc, y, 35);
    y = statementTable(doc, y, 'Cash and bank (does not change profit)', cashBankRows(ov.cash_bank));
    y = ensureSpace(doc, y, 30);
    const head = ['Branch', 'Net sales', 'COGS', 'Gross profit', 'Operating exp.', 'Net profit', 'Margin'];
    const row = (n, r) => { const q = r.profit || {}; return [n, SR(q.net_sales ?? r.sales?.net), SR(q.cogs ?? r.cogs), SR(q.gross_profit), SR(q.operating_expenses ?? r.expenses_total), SR(r.net), `${r.net_margin ?? 0}%`]; };
    autoTable(doc, {
      startY: y, head: [head], body: [...data.branches.map((b) => row(b.branch_name, b)), row('TOTAL', g)],
      margin: { left: 12, right: 12 }, rowPageBreak: 'avoid',
      styles: { fontSize: 8, cellPadding: 1.8, lineColor: [235, 235, 235], lineWidth: 0.1 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
      columnStyles: Object.fromEntries(head.map((_, i) => [i, { halign: i ? 'right' : 'left' }])),
      didParseCell: (h) => { if (h.section === 'body' && h.row.index === data.branches.length) { h.cell.styles.fontStyle = 'bold'; h.cell.styles.fillColor = [241, 245, 249]; } },
    });
    addFooter(doc);
    const tag = (opts.branchNames || 'all-branches').replace(/[^A-Za-z0-9]+/g, '-').slice(0, 40);
    doc.save(`branch_summary_${data.period?.start || ''}_to_${data.period?.end || ''}_${tag}.pdf`);
    toast.success('PDF exported');
  } catch (err) { console.error(err); toast.error('Failed to generate PDF'); }
}

// Keep backward compatibility
export async function exportDashboardToPDF(elementId, title, subtitle) {
  const element = document.getElementById(elementId);
  if (!element) { toast.error('Nothing to export'); return; }
  toast.info('Generating PDF...');
  const html2canvas = (await import('html2canvas')).default;
  try {
    const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#fff', windowWidth: 1200 });
    const imgData = canvas.toDataURL('image/jpeg', 0.85);
    const doc = new jsPDF('p', 'mm', 'a4');
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    addHeader(doc, title, subtitle);
    const imgW = w - 20;
    const imgH = (canvas.height * imgW) / canvas.width;
    let y = 28;
    let rem = imgH;
    let srcY = 0;
    while (rem > 0) {
      const avail = h - y - 10;
      const slice = Math.min(avail, rem);
      const ratio = slice / imgH;
      const srcH = canvas.height * ratio;
      const sc = document.createElement('canvas');
      sc.width = canvas.width; sc.height = srcH;
      sc.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
      doc.addImage(sc.toDataURL('image/jpeg', 0.85), 'JPEG', 10, y, imgW, slice);
      rem -= slice; srcY += srcH;
      if (rem > 0) { doc.addPage(); y = 10; }
    }
    addFooter(doc);
    doc.save(`${(title || 'report').replace(/\s+/g, '_').toLowerCase()}_${localToday()}.pdf`);
    toast.success('PDF exported');
  } catch (err) { console.error(err); toast.error('Failed'); }
}


/**
 * Partner Statement of Account — two-section PDF showing capital vs operating flows.
 * Used for year-end family / holding-company partner reporting.
 */
export function exportPartnerStatementToPDF(stmt) {
  if (!stmt?.partner) { toast.error('No statement data to export'); return; }
  toast.info('Generating statement...');

  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const w = doc.internal.pageSize.getWidth();
    const pr = stmt.partner;
    const cap = stmt.capital_account || {};
    const op = stmt.operating_account || {};
    const loan = stmt.loan_account || {};

    addHeader(doc, 'Partner Statement of Account', `${pr.name} • Share ${pr.share_percentage}%`);

    let y = 30;
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${stmt.period?.start_date || ''}  to  ${stmt.period?.end_date || ''}`, 10, y);
    if (pr.phone || pr.email) {
      const contact = [pr.phone, pr.email].filter(Boolean).join('  •  ');
      doc.text(contact, w - 10, y, { align: 'right' });
    }
    y += 6;

    // KPI strip
    const kpis = [
      ['Opening Capital', SR(cap.opening_balance), [30, 58, 138]],
      ['Net Capital Change', SR(cap.net_change), (cap.net_change || 0) >= 0 ? [34, 197, 94] : [239, 68, 68]],
      ['Closing Capital', SR(cap.closing_balance), (cap.closing_balance || 0) >= 0 ? [34, 197, 94] : [239, 68, 68]],
      ['Loan Balance', SR(loan.closing_balance), (loan.closing_balance || 0) > 0 ? [245, 158, 11] : [100, 116, 139]],
    ];
    const boxW = (w - 20 - 9) / 4;
    kpis.forEach(([label, value, color], i) => {
      const x = 10 + i * (boxW + 3);
      doc.setFillColor(...color);
      doc.rect(x, y, boxW, 16, 'F');
      doc.setTextColor(255);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(label.toUpperCase(), x + 3, y + 5);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(value, x + 3, y + 12);
    });
    y += 22;

    // Capital Account
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Capital Account', 10, y);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Investments, withdrawals & profit share', 10, y + 4);
    y += 7;

    const capBody = (cap.rows || []).map(r => [
      r.date,
      (r.type || '').replace('_', ' '),
      r.description || '-',
      r.payment_mode || '-',
      r.direction === 'in' ? SR(r.amount) : '',
      r.direction === 'out' ? SR(r.amount) : '',
    ]);
    capBody.unshift([
      { content: 'Opening Balance', colSpan: 5, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } },
      { content: SR(cap.opening_balance), styles: { fontStyle: 'bold', fillColor: [241, 245, 249], halign: 'right' } },
    ]);
    if (!cap.rows || cap.rows.length === 0) {
      capBody.push([{ content: 'No capital movements in this period.', colSpan: 6, styles: { fontStyle: 'italic', textColor: [120, 120, 120], halign: 'center' } }]);
    }
    capBody.push([
      { content: 'Period Total', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [220, 230, 245] } },
      { content: SR(cap.total_in), styles: { fontStyle: 'bold', fillColor: [220, 230, 245], halign: 'right', textColor: [21, 128, 61] } },
      { content: SR(cap.total_out), styles: { fontStyle: 'bold', fillColor: [220, 230, 245], halign: 'right', textColor: [185, 28, 28] } },
    ]);
    capBody.push([
      { content: 'Closing Capital Balance', colSpan: 5, styles: { fontStyle: 'bold', fillColor: [15, 23, 42], textColor: 255 } },
      { content: SR(cap.closing_balance), styles: { fontStyle: 'bold', fillColor: [15, 23, 42], textColor: 255, halign: 'right' } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Date', 'Type', 'Description', 'Mode', 'In (SR)', 'Out (SR)']],
      body: capBody,
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' } },
      margin: { left: 10, right: 10 },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Operating Account
    if (y > 240) { doc.addPage(); y = 15; }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Operating Payouts', 10, y);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Salary, advances, loan repayments — does not affect capital', 10, y + 4);
    y += 7;

    const opBody = (op.rows || []).map(r => [
      r.date,
      (r.type || '').replace('_', ' '),
      r.period || '-',
      r.description || '-',
      r.payment_mode || '-',
      SR(r.amount),
    ]);
    if (!op.rows || op.rows.length === 0) {
      opBody.push([{ content: 'No operating payouts in this period.', colSpan: 6, styles: { fontStyle: 'italic', textColor: [120, 120, 120], halign: 'center' } }]);
    }
    opBody.push([
      { content: `Salary: ${SR(op.salary_total)}  •  Advances: ${SR(op.advance_total)}  •  Repayments: ${SR(op.repayment_total)}`, colSpan: 5, styles: { fontStyle: 'bold', fillColor: [255, 237, 213], fontSize: 7 } },
      { content: SR(op.net_paid), styles: { fontStyle: 'bold', fillColor: [255, 237, 213], halign: 'right' } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Date', 'Type', 'Period', 'Description', 'Mode', 'Amount (SR)']],
      body: opBody,
      theme: 'grid',
      headStyles: { fillColor: [234, 88, 12], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 5: { halign: 'right' } },
      margin: { left: 10, right: 10 },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Loan Account (only if any activity)
    if ((loan.opening_balance || 0) !== 0 || (loan.taken || 0) > 0 || (loan.repaid || 0) > 0) {
      if (y > 250) { doc.addPage(); y = 15; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Loan Account', 10, y);
      y += 5;
      autoTable(doc, {
        startY: y,
        head: [['Movement', 'Amount (SR)']],
        body: [
          ['Opening Loan Balance', SR(loan.opening_balance)],
          ['+ Advances Taken', SR(loan.taken)],
          ['− Repayments', SR(loan.repaid)],
          [
            { content: 'Closing Loan Balance', styles: { fontStyle: 'bold', fillColor: [254, 243, 199] } },
            { content: SR(loan.closing_balance), styles: { fontStyle: 'bold', fillColor: [254, 243, 199], halign: 'right' } },
          ],
        ],
        theme: 'grid',
        headStyles: { fillColor: [245, 158, 11], fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 1: { halign: 'right' } },
        margin: { left: 10, right: 10 },
      });
    }

    // Monthly Salary Summary — Month | Entitled | Paid | Outstanding Balance.
    // Entitled uses the pro-rata history engine per month (Jan=15k, Aug=20k)
    // via stmt.monthly_entitlements, with the flat salary as fallback.
    const entitled = Number(pr.salary || 0);
    const entitlements = stmt.monthly_entitlements || {};
    if ((entitled > 0 || Object.keys(entitlements).length > 0) && stmt.period?.start_date && stmt.period?.end_date) {
      const MONTHS_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const sd = new Date(stmt.period.start_date);
      const ed = new Date(stmt.period.end_date);
      const monthRows = [];
      const iter = new Date(sd.getFullYear(), sd.getMonth(), 1);
      let outstanding = 0;
      let guard = 0;
      while (iter <= ed && guard < 36) {
        const lbl = `${MONTHS_S[iter.getMonth()]} ${iter.getFullYear()}`;
        const paid = (op.rows || [])
          .filter((r) => r.type === 'salary' && r.period === lbl)
          .reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const entMonth = entitlements[lbl] != null ? Number(entitlements[lbl]) : entitled;
        outstanding += entMonth - paid;
        monthRows.push([lbl, SR(entMonth), SR(paid), SR(outstanding)]);
        iter.setMonth(iter.getMonth() + 1);
        guard += 1;
      }
      if (monthRows.length) {
        y = doc.lastAutoTable.finalY + 8;
        if (y > 235) { doc.addPage(); y = 15; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text('Monthly Salary Summary', 10, y);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Entitled vs paid per month with running outstanding balance', 10, y + 4);
        y += 7;
        autoTable(doc, {
          startY: y,
          head: [['Month', 'Entitled (SR)', 'Paid (SR)', 'Outstanding Balance (SR)']],
          body: monthRows,
          foot: [['Total Due', '', '', SR(outstanding)]],
          theme: 'grid',
          headStyles: { fillColor: [15, 118, 110], fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          footStyles: { fillColor: [127, 29, 29], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold', halign: 'right' },
          columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
          margin: { left: 10, right: 10 },
        });
      }
    }

    addFooter(doc);
    const safeName = pr.name.replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    doc.save(`partner_statement_${safeName}_${stmt.period?.start_date}_${stmt.period?.end_date}.pdf`);
    toast.success('Statement downloaded');
  } catch (err) {
    console.error(err);
    toast.error('Failed to generate statement');
  }
}

/** iter939 — Internal Settlement Statement PDF. Reuses the Tax-Invoice layout
 *  (addHeader/addFooter + autoTable) but is stamped 'INTERNAL - NOT A TAX
 *  INVOICE' and carries a Daily Breakdown of items moved (Burger, Cheese…).
 *  NON-REVENUE — never a ZATCA tax document. */
export function exportInternalStatementToPDF(stmt, company = {}, periodLabel = '', currency = 'SR') {
  if (!stmt) { toast.error('No statement to export'); return false; }
  const fmt = _fmtCur(currency);
  toast.info('Generating internal statement…');
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const w = doc.internal.pageSize.getWidth();
    addHeader(doc, company.company_name || 'Smarto', 'INTERNAL SETTLEMENT STATEMENT');

    // Prominent NON-TAX watermark banner (reuses the invoice header band style).
    let y = 30;
    doc.setFillColor(254, 243, 199);
    doc.rect(0, y - 4, w, 8, 'F');
    doc.setTextColor(180, 83, 9);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('INTERNAL - NOT A TAX INVOICE', w / 2, y + 1.5, { align: 'center' });
    y += 12;

    doc.setTextColor(40);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${stmt.from_branch_name}  →  ${stmt.to_branch_name}`, 10, y);
    doc.setFont('helvetica', 'normal');
    doc.text(periodLabel || '', w - 10, y, { align: 'right' });
    y += 5;
    doc.text('Excluded from Sales / VAT reports', 10, y);
    y += 4;

    // Line items (per transfer) — mirrors the invoice item table.
    const lineBody = [];
    (stmt.lines || []).forEach((l) => {
      const itemText = (l.items || []).length
        ? (l.items || []).map((it) => `${it.item_name} ×${it.quantity}`).join(', ')
        : (l.description || 'Stock transfer');
      lineBody.push([String(l.date || '').slice(0, 10), itemText, fmt(l.amount)]);
    });
    autoTable(doc, {
      startY: y + 2,
      head: [['Date', 'Items Moved', 'Value']],
      body: lineBody.length ? lineBody : [['—', 'No transfers', fmt(0)]],
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [15, 23, 42], fontSize: 8 },
      columnStyles: { 0: { cellWidth: 26 }, 2: { halign: 'right', cellWidth: 30 } },
      margin: { left: 10, right: 10 },
      foot: [[{ content: 'Total Transferred', colSpan: 2, styles: { fontStyle: 'bold', halign: 'right' } }, { content: fmt(stmt.total_value), styles: { fontStyle: 'bold', halign: 'right' } }]],
    });

    // Daily Breakdown of items (Burger, Cheese, …) — the requested validation.
    let dy = doc.lastAutoTable.finalY + 8;
    if ((stmt.daily_breakdown || []).length) {
      if (dy > doc.internal.pageSize.getHeight() - 50) { doc.addPage(); dy = 20; }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Daily Breakdown — Items Moved', 10, dy);
      dy += 3;
      const dailyBody = [];
      (stmt.daily_breakdown || []).forEach((d) => {
        (d.items || []).forEach((it, idx) => {
          dailyBody.push([idx === 0 ? String(d.date).slice(0, 10) : '', it.item_name, String(it.quantity)]);
        });
      });
      autoTable(doc, {
        startY: dy + 2,
        head: [['Date', 'Item', 'Qty Moved']],
        body: dailyBody.length ? dailyBody : [['—', 'No items', '0']],
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [234, 88, 12], fontSize: 8 },
        columnStyles: { 0: { cellWidth: 28 }, 2: { halign: 'center', cellWidth: 26 } },
        margin: { left: 10, right: 10 },
      });
      dy = doc.lastAutoTable.finalY + 6;
    }

    // Item totals + outstanding to settle.
    if ((stmt.item_summary || []).length) {
      if (dy > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); dy = 20; }
      autoTable(doc, {
        startY: dy,
        head: [['Item', 'Total Qty', 'Total Value']],
        body: (stmt.item_summary || []).map((s) => [`${s.item_name} (${s.unit})`, String(s.total_qty), fmt(s.total_value)]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [15, 118, 110], fontSize: 8 },
        columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } },
        margin: { left: 10, right: 10 },
        foot: [[{ content: 'Outstanding to Settle', styles: { fontStyle: 'bold' } }, { content: '', styles: {} }, { content: fmt(stmt.outstanding), styles: { fontStyle: 'bold', halign: 'right' } }]],
        footStyles: { fillColor: [255, 237, 213], textColor: [120, 53, 15] },
      });
    }

    addFooter(doc);
    const safe = `${stmt.from_branch_name}_${stmt.to_branch_name}`.replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    doc.save(`internal_statement_${safe}_${(periodLabel || localToday()).replace(/[^a-z0-9]+/gi, '_')}.pdf`);
    toast.success('Internal statement downloaded');
    return true;
  } catch (err) {
    console.error(err);
    toast.error('Failed to generate statement');
    return false;
  }
}

/** iter912/913 — Branded Tax-Invoice PDFs: single download, merged audit
 *  bundle and base64-for-email. Reuses the handleExport check-toast pattern;
 *  ZATCA QR is anchored at the bottom of each invoice page. */
const _fmtCur = (currency) => (v) => `${currency} ${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

async function _qrDataUrl(qrData) {
  if (!qrData) return null;
  try { return await QRCode.toDataURL(qrData, { margin: 0, width: 256 }); } catch { return null; }
}

function _renderInvoicePage(doc, inv, company, qrDataUrl, currency) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const fmt = _fmtCur(currency);
  const grand = Number(inv.total_with_vat || inv.total) || 0;
  addHeader(doc, company.company_name || 'Smarto', 'TAX INVOICE');

  let y = 32;
  doc.setTextColor(40);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(`Invoice # ${inv.invoice_number || ''}`, 10, y);
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${String(inv.date || '').slice(0, 10)}`, w - 10, y, { align: 'right' });
  y += 5;
  doc.text(`Customer: ${inv.customer_name || 'Walk-in'}`, 10, y);
  doc.text(`Payment: ${inv.payment_mode || '-'}`, w - 10, y, { align: 'right' });
  y += 5;
  if (company.vat_number) { doc.text(`Seller VAT No: ${company.vat_number}`, 10, y); y += 5; }
  if (inv.buyer_vat_number) { doc.text(`Buyer VAT No: ${inv.buyer_vat_number}`, 10, y); y += 5; }

  autoTable(doc, {
    startY: y + 2,
    head: [['#', 'Description', 'Qty', 'Unit Price', 'Total']],
    body: (inv.items || []).map((it, i) => [
      i + 1,
      it.description || it.name || '',
      it.quantity,
      fmt(it.unit_price),
      fmt(it.total ?? (Number(it.quantity) || 0) * (Number(it.unit_price) || 0)),
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [15, 23, 42], fontSize: 8 },
    columnStyles: { 0: { cellWidth: 8 }, 2: { halign: 'center', cellWidth: 15 }, 3: { halign: 'right', cellWidth: 28 }, 4: { halign: 'right', cellWidth: 28 } },
    margin: { left: 10, right: 10 },
  });

  let ty = doc.lastAutoTable.finalY + 6;
  if (ty > h - 85) { doc.addPage(); ty = 30; }
  const totals = [['Subtotal (excl VAT)', fmt(inv.subtotal)]];
  if (inv.discount > 0) totals.push(['Discount', `-${fmt(inv.discount)}`]);
  totals.push(['Taxable Amount', fmt(inv.total)]);
  if ((inv.vat_amount || 0) > 0) totals.push([`VAT (${inv.vat_rate || 15}%)`, fmt(inv.vat_amount)]);
  totals.push(['TOTAL', fmt(grand)]);
  doc.setTextColor(40);
  totals.forEach(([label, val], i) => {
    const last = i === totals.length - 1;
    if (last) { doc.setDrawColor(120); doc.line(w - 75, ty - 3.5, w - 10, ty - 3.5); }
    doc.setFont('helvetica', last ? 'bold' : 'normal');
    doc.setFontSize(last ? 11 : 9);
    doc.text(label, w - 75, ty);
    doc.text(val, w - 10, ty, { align: 'right' });
    ty += last ? 0 : 5;
  });

  if (qrDataUrl) {
    const qrY = h - 54;
    doc.setDrawColor(210);
    doc.line(10, qrY - 4, w - 10, qrY - 4);
    doc.addImage(qrDataUrl, 'PNG', 10, qrY, 34, 34);
    doc.setTextColor(90);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('ZATCA E-Invoice QR', 48, qrY + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`Seller: ${company.company_name || 'Smarto'}`, 48, qrY + 11);
    if (company.vat_number) doc.text(`VAT No: ${company.vat_number}`, 48, qrY + 15);
    doc.text('Scan to verify this tax invoice (ZATCA Phase 1 TLV).', 48, qrY + 20);
  }
}

async function _buildInvoiceDoc(inv, company, qrData, currency) {
  const doc = new jsPDF('p', 'mm', 'a4');
  _renderInvoicePage(doc, inv, company, await _qrDataUrl(qrData), currency);
  addFooter(doc);
  return doc;
}

export async function exportInvoiceToPDF(inv, company = {}, qrData = null, currency = 'SR') {
  const items = inv?.items || [];
  if (!items.length) { toast.info('No line items to export'); return false; }
  const grand = Number(inv.total_with_vat || inv.total) || 0;
  toast.success(`Checked ${items.length} items · ${grand.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  try {
    const doc = await _buildInvoiceDoc(inv, company, qrData, currency);
    doc.save(`Invoice_${inv.invoice_number || inv.id}.pdf`);
    return true;
  } catch (err) {
    console.error(err);
    toast.error('Failed to generate invoice PDF');
    return false;
  }
}

/** Base64 (no data: prefix) of the branded single-invoice PDF — used for emailing. */
export async function invoicePdfBase64(inv, company = {}, qrData = null, currency = 'SR') {
  const doc = await _buildInvoiceDoc(inv, company, qrData, currency);
  return doc.output('datauristring').split(',')[1];
}

/** Merged audit bundle: cover summary table + one branded page per invoice. */
export async function exportInvoicesMergedPDF(invoices, company = {}, qrMap = {}, currency = 'SR') {
  if (!invoices?.length) { toast.info('No invoices selected'); return false; }
  const fmt = _fmtCur(currency);
  const grandAll = invoices.reduce((s, x) => s + (Number(x.total_with_vat || x.total) || 0), 0);
  toast.success(`Checked ${invoices.length} invoices · ${grandAll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    addHeader(doc, company.company_name || 'Smarto', `INVOICE AUDIT BUNDLE — ${invoices.length} invoices`);
    doc.setTextColor(40);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Audit Summary', 10, 32);
    autoTable(doc, {
      startY: 36,
      head: [['#', 'Invoice #', 'Date', 'Customer', 'Subtotal', 'VAT', 'Total']],
      body: invoices.map((x, i) => [
        i + 1, x.invoice_number || '', String(x.date || '').slice(0, 10), x.customer_name || 'Walk-in',
        fmt(x.subtotal), fmt(x.vat_amount), fmt(x.total_with_vat || x.total),
      ]),
      foot: [['', '', '', 'GRAND TOTAL',
        fmt(invoices.reduce((s, x) => s + (Number(x.subtotal) || 0), 0)),
        fmt(invoices.reduce((s, x) => s + (Number(x.vat_amount) || 0), 0)),
        fmt(grandAll)]],
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [15, 23, 42], fontSize: 8 },
      footStyles: { fillColor: [30, 41, 59], fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
      margin: { left: 10, right: 10 },
    });
    for (const inv of invoices) {
      doc.addPage();
      _renderInvoicePage(doc, inv, company, await _qrDataUrl(qrMap[inv.id]), currency);
    }
    addFooter(doc);
    doc.save(`Invoices_Audit_${new Date().toISOString().slice(0, 10)}_${invoices.length}.pdf`);
    return true;
  } catch (err) {
    console.error(err);
    toast.error('Failed to generate merged PDF');
    return false;
  }
}
